/**
 * TypeScript types for Analytics Dashboard System
 * Matches backend DTOs in com.somospapo.backend.app.analytics.dto
 */

// Enum types
export type GraphType = "BAR" | "PIE" | "SUM" | "CALENDAR_HEATMAP" | "MAP";
export type DataSourceEntity =
  | "FARM"
  | "FARM_ADMIN"
  | "CHALLENGE"
  | "CHALLENGE_FORM"
  | "TRAINING"
  | "TRAINING_ENROLLMENT"
  | "TRAININGS_MODULE"
  | "TRAININGS_MODULES_TOPIC"
  | "TRAININGS_MODULES_TOPICS_CONTENT"
  | "TRAINING_CONTENT_PROGRESS";
export type AggregationType = "COUNT" | "SUM" | "AVG" | "MAX" | "MIN";
export type FilterOperator =
  | "EQUALS"
  | "NOT_EQUALS"
  | "GREATER_THAN"
  | "LESS_THAN"
  | "IN"
  | "BETWEEN";

// New enums for secondary dimension and field transforms
export type DateGranularity = "DAY" | "WEEK" | "MONTH" | "QUARTER" | "YEAR";
export type FieldTransform = "NONE" | "HAS_VALUE" | "BOOLEAN_LABELS";
export type SecondaryDimensionType = "NONE" | "TIME" | "CATEGORICAL";

// Dashboard scope filter types
export type ScopeFilterType = "GROUPS" | "CITIES" | "REGIONS" | "CROPS";

// ============================================================
// CHALLENGE_FORM Hierarchical Field Selection Types
// ============================================================

// Hierarchical program structure (from backend /programs-hierarchy endpoint)
export interface ProgramHierarchyDto {
  id: string;
  name: string;
  versions: ProgramVersionHierarchyDto[];
}

export interface ProgramVersionHierarchyDto {
  id: string;
  name: string;
  programId: string;
  levels: ProgramLevelItemsDto[];
}

export interface ProgramLevelItemsDto {
  id: string;
  name: string;
  items: ProgramItemDto[];
}

export interface ProgramItemDto {
  id: string;
  name: string;
  description?: string;
  measurementType: string;
  measurementTemplate?: string; // Keep for backward compatibility (challenges API)
  fields?: FieldOptionDto[]; // Pre-parsed fields from hierarchy endpoint
  programLevelName: string;
  versionId: string;
  programId: string;
  programLevelId?: string;
  cpNumber?: string;
}

export interface FieldOptionDto {
  key: string; // Primary field identifier from backend
  label: string;
  type: "string" | "number" | "date" | "enum" | "boolean";
  jsonPath?: string;
  enumValues?: string[];
  optionColors?: OptionColorDto[]; // For MAP graph: option value -> color mapping
  questionId?: string; // DEPRECATED - use 'key' instead, kept for backward compatibility
}

export interface OptionColorDto {
  value: string;
  label: string;
  color: string | null; // Hex color or null for auto-assign
}

// Result of field selection from modal (simplified)
export interface ChallengeFormFieldSelection {
  key: string;
  label: string;
  type: string;
  questionId: string;
  forEachContext?: string[];
  itemId?: string;
  versionId?: string;
  programId?: string;
  programName?: string;
  versionName?: string;
  itemName?: string;
  fieldName?: string;
}

// Common response wrapper
export interface CustomResponseEntity<T = any> {
  data: T;
  status: string;
  code: number;
  httpErrorCode: number;
  description: string;
}

// Dashboard DTOs
export interface AnalyticsDashboardDto {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  isActive: boolean;
  scopeFilters: AnalyticsDashboardScopeFilterDto[];
  graphs: AnalyticsGraphDto[];
}

export interface AnalyticsDashboardScopeFilterDto {
  id?: string;
  dashboardId?: string;
  filterType: ScopeFilterType;
  filterValue: string; // JSON array string: ["value1", "value2"]
}

export interface AnalyticsDashboardCreateDto {
  name: string;
  description?: string;
  scopeFilters?: AnalyticsDashboardScopeFilterDto[];
}

export interface AnalyticsDashboardUpdateDto {
  name: string;
  description?: string;
}

// Graph DTOs
export interface AnalyticsGraphDto {
  id: string;
  dashboardId: string;
  name: string;
  description: string;
  graphType: GraphType;
  dataSourceEntity: DataSourceEntity;
  dataSourceField: string;
  aggregationType: AggregationType;
  // Secondary dimension for grouping (time-series or categorical)
  secondaryDimensionType?: SecondaryDimensionType;
  secondaryDimensionField?: string;
  secondaryDimensionGranularity?: DateGranularity;
  // Field transformation for semantic grouping
  fieldTransform?: FieldTransform;
  positionIndex: number;
  filters: AnalyticsGraphFilterDto[];
}

export interface AnalyticsGraphCreateDto {
  name: string;
  description?: string;
  graphType: GraphType;
  dataSourceEntity: DataSourceEntity;
  dataSourceField: string;
  aggregationType: AggregationType;
  // Secondary dimension for grouping (time-series or categorical)
  secondaryDimensionType?: SecondaryDimensionType;
  secondaryDimensionField?: string;
  secondaryDimensionGranularity?: DateGranularity;
  // Field transformation for semantic grouping
  fieldTransform?: FieldTransform;
  positionIndex?: number;
  filters?: AnalyticsGraphFilterDto[];
}

export interface AnalyticsGraphUpdateDto {
  name: string;
  description?: string;
  graphType: GraphType;
  dataSourceEntity: DataSourceEntity;
  dataSourceField: string;
  aggregationType: AggregationType;
  // Secondary dimension for grouping (time-series or categorical)
  secondaryDimensionType?: SecondaryDimensionType;
  secondaryDimensionField?: string;
  secondaryDimensionGranularity?: DateGranularity;
  // Field transformation for semantic grouping
  fieldTransform?: FieldTransform;
  positionIndex?: number;
  filters?: AnalyticsGraphFilterDto[];
}

export interface AnalyticsGraphFilterDto {
  id?: string;
  graphId?: string;
  filterField: string;
  filterOperator: FilterOperator;
  filterValue?: string;
  filterValue2?: string; // For BETWEEN operator
}

// UI-specific simplified filter state for the new filter card UI
export interface GraphFilterUI {
  filterField: string;
  operator: FilterOperator;
  values: string[]; // Always array for consistency
}

// Graph Data DTOs
export interface AnalyticsGraphDataRequestDto {
  graphId: string;
  graphType: GraphType;
  dataSourceEntity: DataSourceEntity;
  dataSourceField: string;
  aggregationType: AggregationType;
  // Secondary dimension for grouping (time-series or categorical)
  secondaryDimensionType?: SecondaryDimensionType;
  secondaryDimensionField?: string;
  secondaryDimensionGranularity?: DateGranularity;
  // Field transformation for semantic grouping
  fieldTransform?: FieldTransform;
  scopeFilters: AnalyticsDashboardScopeFilterDto[]; // Dashboard-level filters
  filters: AnalyticsGraphFilterDto[]; // Graph-level filters
  organizationId: string;
}

export interface AnalyticsGraphDataResponseDto {
  graphId: string;
  graphType: GraphType;
  data: GraphDataDto;
}

export type GraphDataDto =
  | BarChartDataDto
  | PieChartDataDto
  | SumDataDto
  | CalendarHeatmapDataDto
  | CalendarHeatmapWithDimensionDataDto
  | MapDataDto;

export interface BarChartDataDto {
  labels: string[];
  values: number[];
  colors: string[];
}

export interface PieChartDataDto {
  labels: string[];
  values: number[];
  colors: string[];
}

export interface SumDataDto {
  value: number;
  label: string;
}

export interface CalendarHeatmapDataDto {
  days: CalendarDayDto[];
  year: string;
  label: string;
}

export interface CalendarDayDto {
  date: string;
  count: number;
  level: number; // 0-4 intensity level
}

export interface CalendarHeatmapWithDimensionDataDto {
  rows: CalendarRowDto[];
  year: string;
  label: string;
  secondaryDimensionField: string;
  weeks: number[]; // [1, 2, 3, ..., 53]
}

export interface CalendarRowDto {
  rowLabel: string; // Display label
  rowValue: string; // Raw value for drill-down
  cells: CalendarCellDto[];
}

export interface CalendarCellDto {
  count: number;
  level: number; // 0-4
}

// MAP graph data types
export interface MapDataDto {
  points: MapPointDto[];
  metadata: MapGraphMetadataDto;
}

export interface MapPointDto {
  farmId: string;
  farmName: string;
  latitude: number;
  longitude: number;
  colorValue: string; // SELECT answer value
  color: string; // Resolved hex color
  transparencyValue: number | null; // NUMBER/DATE answer value
  opacity: number; // Computed opacity 0.7 - 1.0
}

export interface MapGraphMetadataDto {
  minValue: number | null;
  maxValue: number | null;
  colorLegend: ColorLegendItemDto[];
  transparencyLabel: string | null;
  transparencyFieldType: string | null;
  excludedNoCoordinates: number;
  excludedNoAnswer: number;
}

export interface ColorLegendItemDto {
  optionValue: string;
  optionLabel: string;
  color: string;
  count: number;
}

// Drill-down DTOs
export interface DrillDownDataDto {
  filterValue: string;
  farms: FarmSummaryDto[];
}

export interface FarmSummaryDto {
  farmId: string;
  farmName: string;
  value: string; // Display value for the farm
}

// Field metadata
export interface FieldMetadataDto {
  key: string; // Unique identifier for the field
  label: string; // Human-readable label
  type: "string" | "number" | "date" | "enum" | "boolean";
  dataSourceEntity: DataSourceEntity;
  path: string; // JPA path for accessing the field
  enumValues?: string[]; // For enum types, list of possible values
  category?: string; // Optional category for grouping in UI
}

// Scope filter options
export interface ScopeFilterOptionDto {
  value: string;
  label: string;
}

// ============================================================
// Data Source Catalog (from /fields-catalog endpoint)
// Single source of truth for field metadata, labels, and reachability
// ============================================================

export interface FieldDef {
  key: string;
  label: string;
  kind: "DIMENSION" | "MEASURE" | "DATE";
  valueType: "string" | "number" | "date" | "enum" | "boolean";
  enumValues?: string[];
  category?: string;
  jpaPath?: string;
}

export interface DataSourceCatalogDto {
  id: DataSourceEntity;
  label: string;
  fields: FieldDef[];
  reachableSources: DataSourceEntity[];
  supportsMap: boolean;
}

// Pre-defined common fields for each entity
export const PREDEFINED_FIELDS: Record<DataSourceEntity, FieldMetadataDto[]> = {
  FARM: [
    {
      key: "name",
      label: "Farm Name",
      type: "string",
      dataSourceEntity: "FARM",
      path: "name",
      category: "Basic",
    },
    {
      key: "region",
      label: "Region",
      type: "string",
      dataSourceEntity: "FARM",
      path: "farmLocations.city.region.name",
      category: "Location",
    },
    {
      key: "city",
      label: "City",
      type: "string",
      dataSourceEntity: "FARM",
      path: "farmLocations.city.name",
      category: "Location",
    },
    {
      key: "area",
      label: "Area (hectares)",
      type: "number",
      dataSourceEntity: "FARM",
      path: "farmLocations.area",
      category: "Location",
    },
    {
      key: "crop",
      label: "Crop Type",
      type: "string",
      dataSourceEntity: "FARM",
      path: "farmCrops.name",
      category: "Crops",
    },
  ],
  FARM_ADMIN: [
    {
      key: "name",
      label: "Admin Name",
      type: "string",
      dataSourceEntity: "FARM_ADMIN",
      path: "name",
      category: "Basic",
    },
    {
      key: "gender",
      label: "Gender",
      type: "enum",
      dataSourceEntity: "FARM_ADMIN",
      path: "gender",
      enumValues: ["M", "F", "Otro"],
      category: "Basic",
    },
    {
      key: "birthdate",
      label: "Birthdate",
      type: "date",
      dataSourceEntity: "FARM_ADMIN",
      path: "birthdate",
      category: "Basic",
    },
    {
      key: "email",
      label: "Email",
      type: "string",
      dataSourceEntity: "FARM_ADMIN",
      path: "email",
      category: "Contact",
    },
  ],
  CHALLENGE: [
    {
      key: "name",
      label: "Inspection Name",
      type: "string",
      dataSourceEntity: "CHALLENGE",
      path: "name",
      category: "Basic",
    },
    {
      key: "startDate",
      label: "Start Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "startDate",
      category: "Dates",
    },
    {
      key: "endDate",
      label: "End Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "endDate",
      category: "Dates",
    },
    {
      key: "closeDate",
      label: "Close Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "closeDate",
      category: "Dates",
    },
  ],
  CHALLENGE_FORM: [], // Populated dynamically from backend
  TRAINING: [
    { key: "name", label: "Training Name", type: "string", dataSourceEntity: "TRAINING", path: "name", category: "Basic" },
    { key: "description", label: "Description", type: "string", dataSourceEntity: "TRAINING", path: "description", category: "Basic" },
    { key: "logo", label: "Logo", type: "string", dataSourceEntity: "TRAINING", path: "logo", category: "Media" },
    { key: "createdAt", label: "Created Date", type: "date", dataSourceEntity: "TRAINING", path: "createdAt", category: "System" },
    { key: "updatedAt", label: "Updated Date", type: "date", dataSourceEntity: "TRAINING", path: "updatedAt", category: "System" },
  ],
  TRAINING_ENROLLMENT: [
    { key: "training.name", label: "Training", type: "string", dataSourceEntity: "TRAINING_ENROLLMENT", path: "training.name", category: "Training" },
    { key: "organization.name", label: "Farm", type: "string", dataSourceEntity: "TRAINING_ENROLLMENT", path: "organization.name", category: "Farm" },
    { key: "group.name", label: "Group", type: "string", dataSourceEntity: "TRAINING_ENROLLMENT", path: "group.name", category: "Farm" },
    { key: "progressPercentage", label: "Progress %", type: "number", dataSourceEntity: "TRAINING_ENROLLMENT", path: "progressPercentage", category: "Progress" },
    { key: "completedAt", label: "Completed Date", type: "date", dataSourceEntity: "TRAINING_ENROLLMENT", path: "completedAt", category: "Progress" },
    { key: "createdAt", label: "Enrolled Date", type: "date", dataSourceEntity: "TRAINING_ENROLLMENT", path: "createdAt", category: "System" },
  ],
  TRAININGS_MODULE: [
    { key: "training.name", label: "Training", type: "string", dataSourceEntity: "TRAININGS_MODULE", path: "training.name", category: "Training" },
    { key: "name", label: "Module Name", type: "string", dataSourceEntity: "TRAININGS_MODULE", path: "name", category: "Basic" },
    { key: "skillName", label: "Skill", type: "string", dataSourceEntity: "TRAININGS_MODULE", path: "skillName", category: "Basic" },
    { key: "sequence", label: "Sequence", type: "number", dataSourceEntity: "TRAININGS_MODULE", path: "sequence", category: "Order" },
    { key: "progressContribution", label: "Progress Contribution", type: "number", dataSourceEntity: "TRAININGS_MODULE", path: "progressContribution", category: "Progress" },
    { key: "createdAt", label: "Created Date", type: "date", dataSourceEntity: "TRAININGS_MODULE", path: "createdAt", category: "System" },
  ],
  TRAININGS_MODULES_TOPIC: [
    { key: "module.training.name", label: "Training", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPIC", path: "module.training.name", category: "Training" },
    { key: "module.name", label: "Module", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPIC", path: "module.name", category: "Training" },
    { key: "name", label: "Topic Name", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPIC", path: "name", category: "Basic" },
    { key: "sequence", label: "Sequence", type: "number", dataSourceEntity: "TRAININGS_MODULES_TOPIC", path: "sequence", category: "Order" },
    { key: "progressContribution", label: "Progress Contribution", type: "number", dataSourceEntity: "TRAININGS_MODULES_TOPIC", path: "progressContribution", category: "Progress" },
  ],
  TRAININGS_MODULES_TOPICS_CONTENT: [
    { key: "topic.module.training.name", label: "Training", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPICS_CONTENT", path: "topic.module.training.name", category: "Training" },
    { key: "topic.module.name", label: "Module", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPICS_CONTENT", path: "topic.module.name", category: "Training" },
    { key: "topic.name", label: "Topic", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPICS_CONTENT", path: "topic.name", category: "Training" },
    { key: "name", label: "Content Name", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPICS_CONTENT", path: "name", category: "Basic" },
    { key: "type.name", label: "Content Type", type: "string", dataSourceEntity: "TRAININGS_MODULES_TOPICS_CONTENT", path: "type.name", category: "Basic" },
    { key: "durationMinutes", label: "Duration (min)", type: "number", dataSourceEntity: "TRAININGS_MODULES_TOPICS_CONTENT", path: "durationMinutes", category: "Content" },
    { key: "sequence", label: "Sequence", type: "number", dataSourceEntity: "TRAININGS_MODULES_TOPICS_CONTENT", path: "sequence", category: "Order" },
  ],
  TRAINING_CONTENT_PROGRESS: [
    { key: "enrollment.training.name", label: "Training", type: "string", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "enrollment.training.name", category: "Training" },
    { key: "enrollment.organization.name", label: "Farm", type: "string", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "enrollment.organization.name", category: "Farm" },
    { key: "enrollment.group.name", label: "Group", type: "string", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "enrollment.group.name", category: "Farm" },
    { key: "content.name", label: "Content", type: "string", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "content.name", category: "Content" },
    { key: "content.type.name", label: "Content Type", type: "string", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "content.type.name", category: "Content" },
    { key: "isCompleted", label: "Is Completed", type: "boolean", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "isCompleted", category: "Progress" },
    { key: "score", label: "Score", type: "number", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "score", category: "Progress" },
    { key: "completedAt", label: "Completed Date", type: "date", dataSourceEntity: "TRAINING_CONTENT_PROGRESS", path: "completedAt", category: "Progress" },
  ],
};

// Graph types configuration for UI
export const GRAPH_TYPES: { value: GraphType; label: string; description: string }[] = [
  { value: "BAR", label: "Gráfico de Barras", description: "Mostrar datos como barras verticales" },
  { value: "PIE", label: "Gráfico Circular", description: "Mostrar datos como gráfico circular" },
  { value: "SUM", label: "Suma/KPI", description: "Mostrar un número agregado" },
  {
    value: "CALENDAR_HEATMAP",
    label: "Mapa de Calendario",
    description: "Mapa de calor estilo calendario",
  },
  { value: "MAP", label: "Mapa", description: "Mapa interactivo con coordenadas de finca" },
];

// Aggregation types configuration
export const AGGREGATION_TYPES: { value: AggregationType; label: string }[] = [
  { value: "COUNT", label: "Contar" },
  { value: "SUM", label: "Sumar" },
  { value: "AVG", label: "Promedio" },
  { value: "MAX", label: "Máximo" },
  { value: "MIN", label: "Mínimo" },
];

// Filter operators
export const FILTER_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: "EQUALS", label: "Igual A" },
  { value: "NOT_EQUALS", label: "Diferente De" },
  { value: "GREATER_THAN", label: "Mayor Que" },
  { value: "LESS_THAN", label: "Menor Que" },
  { value: "IN", label: "En Lista" },
  { value: "BETWEEN", label: "Entre" },
];

// Scope filter types
export const SCOPE_FILTER_TYPES: { value: ScopeFilterType; label: string }[] = [
  { value: "GROUPS", label: "Grupos" },
  { value: "CITIES", label: "Ciudades" },
  { value: "REGIONS", label: "Regiones" },
  { value: "CROPS", label: "Cultivos" },
];

// Data source labels (Spanish)
export const DATA_SOURCE_LABELS: Record<DataSourceEntity, string> = {
  FARM: "Finca",
  FARM_ADMIN: "Administrador de Finca",
  CHALLENGE: "Inspección",
  CHALLENGE_FORM: "Campo de Formulario",
  TRAINING: "Capacitación",
  TRAINING_ENROLLMENT: "Inscripción a Capacitación",
  TRAININGS_MODULE: "Módulo",
  TRAININGS_MODULES_TOPIC: "Tema",
  TRAININGS_MODULES_TOPICS_CONTENT: "Contenido",
  TRAINING_CONTENT_PROGRESS: "Progreso de Contenido",
};

// Helper functions for formatting
export const getGraphTypeLabel = (type: GraphType): string => {
  return GRAPH_TYPES.find((t) => t.value === type)?.label || type;
};

export const getAggregationTypeLabel = (type: AggregationType): string => {
  return AGGREGATION_TYPES.find((t) => t.value === type)?.label || type;
};

export const getDataSourceLabel = (source: DataSourceEntity): string => {
  return DATA_SOURCE_LABELS[source] || source;
};

// Scope filter type labels (Spanish)
export const SCOPE_FILTER_TYPE_LABELS: Record<ScopeFilterType, string> = {
  GROUPS: "Grupos",
  CITIES: "Ciudades",
  REGIONS: "Regiones",
  CROPS: "Cultivos",
};

export const getScopeFilterTypeLabel = (type: ScopeFilterType): string => {
  return SCOPE_FILTER_TYPE_LABELS[type] || type;
};

// Format value to CamelCase (Title Case)
export const toCamelCase = (value: string): string => {
  if (!value) return value;
  // Handle snake_case or SCREAMING_SNAKE_CASE
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Date granularity for time-series grouping
export const DATE_GRANULARITIES: { value: DateGranularity; label: string }[] = [
  { value: "DAY", label: "Diario" },
  { value: "WEEK", label: "Semanal" },
  { value: "MONTH", label: "Mensual" },
  { value: "QUARTER", label: "Trimestral" },
  { value: "YEAR", label: "Anual" },
];

// Field transformations for semantic grouping
export const FIELD_TRANSFORMS: { value: FieldTransform; label: string; description: string }[] = [
  { value: "NONE", label: "Ninguno", description: "Usar valor original del campo" },
  {
    value: "HAS_VALUE",
    label: "Tiene Valor / Sin Valor",
    description: "Agrupar por presencia de valor (útil para archivos, campos opcionales)",
  },
  { value: "BOOLEAN_LABELS", label: "Sí / No", description: "Convertir valores booleanos a Sí/No" },
];

// Secondary dimension types
export const SECONDARY_DIMENSION_TYPES: {
  value: SecondaryDimensionType;
  label: string;
  description: string;
}[] = [
  { value: "NONE", label: "Ninguna", description: "Sin dimensión secundaria" },
  { value: "TIME", label: "Tiempo", description: "Agrupar por período de tiempo (día, mes, año)" },
  {
    value: "CATEGORICAL",
    label: "Categórica",
    description: "Agrupar por campo categórico (ciudad, región, cultivo)",
  },
];

// Date fields available for time-series (per data source)
export const DATE_FIELDS_BY_SOURCE: Record<DataSourceEntity, FieldMetadataDto[]> = {
  FARM: [
    // FARM doesn't have date fields directly, use inspection dates
    {
      key: "createdAt",
      label: "Created Date",
      type: "date",
      dataSourceEntity: "FARM",
      path: "createdAt",
      category: "System",
    },
  ],
  FARM_ADMIN: [
    {
      key: "birthdate",
      label: "Birthdate",
      type: "date",
      dataSourceEntity: "FARM_ADMIN",
      path: "birthdate",
      category: "Personal",
    },
    {
      key: "createdAt",
      label: "Created Date",
      type: "date",
      dataSourceEntity: "FARM_ADMIN",
      path: "createdAt",
      category: "System",
    },
  ],
  CHALLENGE: [
    {
      key: "startDate",
      label: "Start Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "startDate",
      category: "Dates",
    },
    {
      key: "endDate",
      label: "End Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "endDate",
      category: "Dates",
    },
    {
      key: "closeDate",
      label: "Close Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "closeDate",
      category: "Dates",
    },
    {
      key: "createdAt",
      label: "Created Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "createdAt",
      category: "System",
    },
  ],
  CHALLENGE_FORM: [
    // Uses inspection dates for time-series
    {
      key: "startDate",
      label: "Inspection Start Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "startDate",
      category: "Inspection",
    },
    {
      key: "endDate",
      label: "Inspection End Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "endDate",
      category: "Inspection",
    },
    {
      key: "createdAt",
      label: "Created Date",
      type: "date",
      dataSourceEntity: "CHALLENGE",
      path: "createdAt",
      category: "System",
    },
  ],
  TRAINING: [
    {
      key: "createdAt",
      label: "Created Date",
      type: "date",
      dataSourceEntity: "TRAINING",
      path: "createdAt",
      category: "System",
    },
    {
      key: "updatedAt",
      label: "Updated Date",
      type: "date",
      dataSourceEntity: "TRAINING",
      path: "updatedAt",
      category: "System",
    },
  ],
  TRAINING_ENROLLMENT: [
    {
      key: "completedAt",
      label: "Completed Date",
      type: "date",
      dataSourceEntity: "TRAINING_ENROLLMENT",
      path: "completedAt",
      category: "Progress",
    },
    {
      key: "createdAt",
      label: "Enrolled Date",
      type: "date",
      dataSourceEntity: "TRAINING_ENROLLMENT",
      path: "createdAt",
      category: "System",
    },
  ],
  TRAININGS_MODULE: [
    {
      key: "createdAt",
      label: "Created Date",
      type: "date",
      dataSourceEntity: "TRAININGS_MODULE",
      path: "createdAt",
      category: "System",
    },
  ],
  TRAININGS_MODULES_TOPIC: [],
  TRAININGS_MODULES_TOPICS_CONTENT: [],
  TRAINING_CONTENT_PROGRESS: [
    {
      key: "completedAt",
      label: "Completed Date",
      type: "date",
      dataSourceEntity: "TRAINING_CONTENT_PROGRESS",
      path: "completedAt",
      category: "Progress",
    },
  ],
};
